// src/components/Musteriler.js

import React, { useState, useEffect, useMemo } from 'react';
import CustomerFormModal from './CustomerFormModal';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc, getDocs, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, TextField, InputAdornment, Chip, Stack } from '@mui/material';
import { hizliMusteriEkle } from '../utils/musteri';
import { normalizeTr, aramaMetniUret } from '../utils/search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

const Musteriler = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, "customers"), orderBy("adSoyad", "asc"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setCustomers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    // G7: satırlarda araç plakaları — tek vehicles dinleyicisi, istemcide birleştirilir
    // (232 müşteri / 281 araç ölçeğinde uygun).
    const unsubVehicles = onSnapshot(query(collection(db, "vehicles")), (snapshot) => {
      setVehicles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => { unsubscribe(); unsubVehicles(); };
  }, []);

  // musteriId → plaka listesi
  const aracMap = useMemo(() => {
    const map = {};
    vehicles.forEach(v => {
      if (!v.musteriId) return;
      if (!map[v.musteriId]) map[v.musteriId] = [];
      map[v.musteriId].push(v.plaka || (v.plakaBekleniyor ? 'PLAKA BEKLENİYOR' : ''));
    });
    return map;
  }, [vehicles]);

  // G6: ortak akış — mükerrer kontroller müşteri YAZILMADAN önce; hata modalda gösterilir.
  const handleAddCustomer = async (customerData, vehicleInfo) => {
    await hizliMusteriEkle(customerData, vehicleInfo);
    alert("Müşteri başarıyla eklendi!");
  };

  const handleUpdateCustomer = async (id, updatedData) => {
    // Hata YUTULMAZ: modal açık kalıp gerçek hatayı göstersin.
    const customerDocRef = doc(db, 'customers', id);
    await updateDoc(customerDocRef, updatedData);
    alert("Müşteri bilgileri başarıyla güncellendi.");
  };

  // Müşteri silme (Faz 4): zincir temizliği ARTIK SUNUCUDA.
  // Eski akış araçları tek tek client'tan siliyordu; her silme onVehicleDeleted'ı
  // tetikliyor, o da PLAKAYA göre bulduğu iş emirlerini (başka müşterilerinkiler
  // dahil) siliyordu. Ayrıca boş 'cariIslemler' koleksiyonunu temizliyor, gerçek
  // finansal koleksiyon olan 'transactions'a hiç dokunmuyordu.
  // Burada yalnız müşteri dokümanı silinir; gerisini onCustomerDeleted yapar
  // (iş emirleri + payments alt-koleksiyonu, randevular, transactions,
  // planlanmış ödemeler, araçlar).
  const handleDeleteCustomer = async (e, id, adSoyad) => {
    e.stopPropagation();
    try {
      const sorgu = (koleksiyon, alan) => getDocs(query(collection(db, koleksiyon), where(alan, '==', id)));
      const [vehiclesSnap, workOrdersSnap, appointmentsSnap, transactionsSnap, scheduledSnap] = await Promise.all([
        sorgu('vehicles', 'musteriId'),
        sorgu('workOrders', 'musteriId'),
        sorgu('appointments', 'customerId'),
        sorgu('transactions', 'customerId'),
        sorgu('scheduledPayments', 'musteriId')
      ]);

      const onayMetni =
        `'${adSoyad}' adlı müşteri silinecek.\n\n` +
        `Sunucu, müşteriyle birlikte şunları da kalıcı olarak silecek:\n` +
        `• ${vehiclesSnap.size} araç kaydı\n` +
        `• ${workOrdersSnap.size} iş emri (ödeme kayıtları dahil)\n` +
        `• ${appointmentsSnap.size} randevu (iş emrine bağlı olanlar da)\n` +
        `• ${transactionsSnap.size} cari hareket (borç/tahsilat)\n` +
        `• ${scheduledSnap.size} planlanmış ödeme\n\n` +
        `Temizlik sunucuda çalışır, birkaç saniye sürebilir.\n` +
        `BU İŞLEM GERİ ALINAMAZ. Devam edilsin mi?`;
      if (!window.confirm(onayMetni)) return;

      await deleteDoc(doc(db, 'customers', id));
      alert(`'${adSoyad}' silindi. İlişkili kayıtların temizliği sunucuda sürüyor; liste birkaç saniye içinde güncellenir.`);
    } catch (e) {
      console.error("Müşteri silinirken hata: ", e);
      alert("Müşteri silinirken bir hata oluştu.");
    }
  };

  const handleOpenEditModal = (e, customer) => {
    e.stopPropagation();
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  // G8: Türkçe-normalize çok alanlı arama (ad + telefon + e-posta), null-guard'lı.
  const filteredCustomers = useMemo(() => {
    const q = normalizeTr(searchTerm).trim();
    if (!q) return customers;
    return customers.filter(customer =>
      aramaMetniUret(customer.adSoyad, customer.telefon, customer.email).includes(q)
    );
  }, [customers, searchTerm]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1">Müşteri Yönetimi</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField size="small" variant="outlined" placeholder="Ad, telefon veya e-posta ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddModal}>Yeni Müşteri Ekle</Button>
        </Box>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          {loading ? (<CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />) : (
            <Table stickyHeader>
              <TableHead><TableRow><TableCell>Adı Soyadı</TableCell><TableCell>Telefon</TableCell><TableCell>Araçlar</TableCell><TableCell align="right">Cari Bakiye</TableCell><TableCell>İşlemler</TableCell></TableRow></TableHead>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow hover key={customer.id} onClick={() => navigate(`/musteri/${customer.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{customer.adSoyad}</TableCell>
                    <TableCell>{customer.telefon}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {(aracMap[customer.id] || []).filter(Boolean).map((plaka, i) => (
                          <Chip key={i} label={plaka} size="small" variant="outlined" />
                        ))}
                        {!(aracMap[customer.id] || []).filter(Boolean).length && (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: (customer.toplamBakiye || 0) > 0 ? 'error.main' : 'success.main' }}>
                      {(customer.toplamBakiye || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" startIcon={<EditIcon />} sx={{ mr: 1 }} onClick={(e) => handleOpenEditModal(e, customer)}>Düzenle</Button>
                      {currentUser.role === 'Genel Müdür' && (
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={(e) => handleDeleteCustomer(e, customer.id, customer.adSoyad)}>Sil</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
      <CustomerFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAddCustomer={handleAddCustomer} onUpdateCustomer={handleUpdateCustomer} customerToEdit={editingCustomer} />
    </Box>
  );
};
export default Musteriler;